import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FontWeight, Heading } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { ReportFlowStep } from '../constants';
import { IContext, IProfitAndLossFilters, ProfitAndLossPeriodType } from '../constants/types';
import { useGenerateProfitAndLossReport } from '../../../hooks/mutations/useGenerateProfitAndLossReport';
import { useStore } from '../../../store';
import { StepSwitch } from '../components/stepSwitch';
import { Header } from '../components/header';
import { getInitialFilterObject } from '../helpers';
import { useGenerateCashAndFlowReport } from '../../../hooks/mutations/useGenerateCashAndFlowReport';
import { useGetUncategorizedTotalNumOfRecords } from '../../../hooks/query/useGetUncategorizedTransactionsDataForScheduleCQuery';
import { VIEWS_MAP } from '../../../router/constants';
import { useGenerateExpenseReport } from '../../../hooks/mutations/useGenerateExpenseReport';
import PageLayout from '../../../generic-components/PageLayout/PageLayout';
import { ReportType } from '../components/filtersWrapper';
import { FEATURE_FLAG } from '../../../constants/general';
import { featureFlagMightBeAvailable, validateFeatureFlag } from '../../../services/featureFlagService';

export const ProfitAndLossContext = createContext<IContext | null>(null);

const ProfitAndLoss = () => {
  const [activeStep, setActiveStep] = useState<ReportFlowStep>(ReportFlowStep.LOADER);
  const {
    user: { customerId },
    getPersonalAccount,
  } = useStore((state) => state);

  const {
    customer: { createTime },
  } = useStore((state) => state.summary);
  const [filterObject, setFilterObject] = useState<IProfitAndLossFilters>(getInitialFilterObject(new Date(createTime)));
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });

  const [reportType, setReportType] = useState<ReportType>(ReportType.Detailed);
  const [toggleData, setToggleData] = useState<{ incomeTrxCnt: number; expenseTrxCnt: number }>();
  const [isDatesRangeComplete, setIsDatesRangeComplete] = useState<boolean>(false);
  const location = useLocation();
  const { t } = useTranslation('reportsEN');
  const getQueryStr = (type: string) => {
    if (type === 'INCOME' && location.pathname.includes(VIEWS_MAP.EXPENSES)) return '';
    return `&startTime=${filterObject.period?.startDate ? new Date(filterObject.period?.startDate).toISOString().split('T')[0] : ''}&endTime=${filterObject.period?.endDate ? new Date(filterObject.period?.endDate).toISOString().split('T')[0] : ''}&categorizationType=${type}`;
  };

  const { mutateAsync: getUncategorizedTrxCount } = useGetUncategorizedTotalNumOfRecords();
  const {
    mutateAsync: generateProfitAndLossReport,
    data: profitAndLossReportData,
    isLoading: isGeneratingProfitAndLossReport,
  } = useGenerateProfitAndLossReport({
    reportData: filterObject,
    customerId,
    accountId: getPersonalAccount().accountExternalId,
  });

  const {
    mutateAsync: generateCashAndFlowReport,
    data: cashFlowReportData,
    isLoading: isGeneratingCashFlowReport,
  } = useGenerateCashAndFlowReport({
    reportData: filterObject,
    customerId,
    accountId: getPersonalAccount().accountExternalId,
  });

  const {
    mutateAsync: generateExpenseReport,
    data: expenseReportData,
    isLoading: isGeneratingExpenseReport,
  } = useGenerateExpenseReport({
    reportData: filterObject,
    customerId,
    reportType,
  });
  const featureFlagName = (() => {
    switch (location.pathname.replace('/', '')) {
      case VIEWS_MAP.STATEMENT_OF_CASH_FLOWS:
        return FEATURE_FLAG.CASH_FLOW;
      case VIEWS_MAP.PROFIT_AND_LOSS:
        return FEATURE_FLAG.PROFIT_AND_LOSS;
      case VIEWS_MAP.EXPENSES:
        return FEATURE_FLAG.EXPENSE_REPORTS;
      default:
        return '';
    }
  })();

  const contextData = useMemo(
    () => ({
      isLoading: isGeneratingProfitAndLossReport || isGeneratingCashFlowReport || isGeneratingExpenseReport,
      iframeUrlProfitAndLoss: profitAndLossReportData?.IframeUrl,
      IframeUrlCashFlow: cashFlowReportData?.IframeUrl,
      iframeUrlExpenses: expenseReportData?.IframeUrl,
      profitAndLossFileName: profitAndLossReportData?.parsedName,
      cashFlowFileName: cashFlowReportData?.parsedName,
      expensesFileName: expenseReportData?.parsedName,
      activeStep,
      setActiveStep,
      setFilterObject,
      setIsDatesRangeComplete,
      dateRange,
      filterObject,
      setDateRange,
      setReportType,
      toggleData,
    }),
    [
      isGeneratingProfitAndLossReport,
      isGeneratingExpenseReport,
      isGeneratingCashFlowReport,
      cashFlowReportData,
      profitAndLossReportData,
      expenseReportData,
      activeStep,
      dateRange,
      filterObject,
      reportType,
      toggleData,
    ],
  );
  const generateRelevantReport = () => {
    const canGenerateCustomReport =
      activeStep === ReportFlowStep.REPORT &&
      filterObject.period?.periodType === ProfitAndLossPeriodType.CUSTOM &&
      isDatesRangeComplete;
    const canGenerateReport =
      activeStep === ReportFlowStep.REPORT && filterObject.period?.periodType !== ProfitAndLossPeriodType.CUSTOM;

    if (!canGenerateCustomReport && !canGenerateReport) return;

    switch (location.pathname.replace('/', '')) {
      case VIEWS_MAP.STATEMENT_OF_CASH_FLOWS:
        generateCashAndFlowReport();

        break;
      case VIEWS_MAP.PROFIT_AND_LOSS:
        generateProfitAndLossReport();

        break;
      case VIEWS_MAP.EXPENSES:
        generateExpenseReport();

        break;
      default:
        break;
    }
  };
  useEffect(() => {
    Promise.all([
      getUncategorizedTrxCount({ q: getQueryStr('INCOME'), customerId }),
      getUncategorizedTrxCount({ q: getQueryStr('EXPENSE'), customerId }),
    ]).then((values) => {
      setToggleData({ incomeTrxCnt: values[0], expenseTrxCnt: values[1] });
      const count = values.reduce((accumulator, currentValue) => {
        if (typeof currentValue === 'number') {
          return accumulator + currentValue;
        }
        return accumulator;
      }, 0);
      if (count > 0 && activeStep === ReportFlowStep.REPORT) {
        setActiveStep(ReportFlowStep.CATEGORIZE_TRANSACTIONS);
      }
      generateRelevantReport();
    });
  }, [activeStep, filterObject.period?.periodType, reportType, isDatesRangeComplete, location.pathname]);

  useEffect(() => {
    setFilterObject(getInitialFilterObject(new Date(createTime)));
  }, [location.pathname]);

  useEffect(() => {
    if (!validateFeatureFlag(featureFlagName) && featureFlagMightBeAvailable(featureFlagName)) {
      setActiveStep(ReportFlowStep.REQUIRED_UPGRADE_PLAN);
    } else if (validateFeatureFlag(featureFlagName)) {
      setActiveStep(ReportFlowStep.INTRO);
    }
  }, [location.pathname]);
  const reportHeaderData = (() => {
    switch (location.pathname.replace('/', '')) {
      case VIEWS_MAP.STATEMENT_OF_CASH_FLOWS:
        return { title: t('REPORTS.TITLE.CASH_FLOW'), id: 'cash-flow-report-header' };
      case VIEWS_MAP.PROFIT_AND_LOSS:
        return { title: t('REPORTS.TITLE.PROFIT_AND_LOSS'), id: 'profit-loss-report-header' };
      case VIEWS_MAP.EXPENSES:
        return { title: t('REPORTS.TITLE.EXPENSES'), id: 'expense-report-header' };
      default:
        return { title: '', id: '' };
    }
  })();
  return (
    <PageLayout>
      <div className='w-full h-[calc(100dvh-196px)] px-[48px]'>
        <ProfitAndLossContext.Provider value={contextData}>
          <Heading.H3 fontWeight={FontWeight.MEDIUM} id={reportHeaderData.id} className='text-text-main-primary'>
            {reportHeaderData.title}
          </Heading.H3>
          <div className='flex h-full'>
            {activeStep === ReportFlowStep.CATEGORIZE_TRANSACTIONS ? (
              <StepSwitch activeStep={activeStep} setActiveStep={setActiveStep} />
            ) : (
              <div className='w-full h-full '>
                <div className='bg-surface-primary-bw-00 rounded-lg w-full h-full mt-[16px]'>
                  {activeStep === ReportFlowStep.REPORT && (
                    <Header activeStep={activeStep} filterObject={filterObject} setFilterObject={setFilterObject} />
                  )}
                  <StepSwitch activeStep={activeStep} setActiveStep={setActiveStep} />
                  <div />
                </div>
              </div>
            )}
          </div>
        </ProfitAndLossContext.Provider>
      </div>
    </PageLayout>
  );
};
export default ProfitAndLoss;
